import React from 'react';
import './ProjectCarousel.css';
import Button from './Button';
import { motion } from 'framer-motion';
import { HugeiconsIcon } from '@hugeicons/react';
import { ArrowLeft01Icon, ArrowRight01Icon, FloppyDiskIcon } from '@hugeicons-pro/core-stroke-sharp';
import { projectMeta, projectOrder } from '../projects/data';

function ProjectCarousel({ currentId = projectOrder[0], onSelect = undefined, onViewDetails = undefined }) {
  // Include all projects in the carousel, including Archive
  const carouselOrder = React.useMemo(() => projectOrder.slice(), []);
  const [index, setIndex] = React.useState(() => {
    const i = carouselOrder.indexOf(currentId);
    return i >= 0 ? i : 0;
  });
  const [direction, setDirection] = React.useState(0); // -1 prev, 1 next

  React.useEffect(() => {
    const i = carouselOrder.indexOf(currentId);
    if (i >= 0 && i !== index) setIndex(i);
  }, [currentId, index, carouselOrder]);

  const selectByIndex = (i) => {
    const clamped = (i + carouselOrder.length) % carouselOrder.length;
    const id = carouselOrder[clamped];
    setIndex(clamped);
    if (onSelect) onSelect(id);
  };

  const handlePrev = () => { setDirection(-1); selectByIndex(index - 1); };
  const handleNext = () => { setDirection(1); selectByIndex(index + 1); };

  const id = carouselOrder[index];

  const handleDotClick = (i) => {
    if (i === index) return;
    const diff = i - index;
    setDirection(diff > 0 ? 1 : -1);
    selectByIndex(i);
  };

  const handleDragEnd = (_e, info) => {
    const offsetX = info.offset.x || 0;
    const velocityX = info.velocity.x || 0;
    const threshold = 50;
    // Drag left => next, Drag right => prev
    if (offsetX < -threshold || velocityX < -500) {
      handleNext();
      return;
    }
    if (offsetX > threshold || velocityX > 500) {
      handlePrev();
      return;
    }
  };

  // Track layout: measure viewport and compute slide width
  const viewportRef = React.useRef(null);
  const [viewportWidth, setViewportWidth] = React.useState(0);
  const sidePadding = 24; // match CSS
  const gapPx = 12; // match CSS
  const slideWidth = Math.max(0, viewportWidth - sidePadding * 2);

  React.useEffect(() => {
    const measure = () => {
      if (!viewportRef.current) return;
      setViewportWidth(viewportRef.current.clientWidth || 0);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  return (
    <div className="project-carousel" role="region" aria-label="Project carousel">

      <div className="pc-content-viewport" ref={viewportRef}>
        <motion.div
          className="pc-track"
          drag="x"
          dragElastic={0.2}
          dragMomentum={false}
          onDragEnd={handleDragEnd}
          animate={{ x: -(slideWidth + gapPx) * index }}
          transition={{ type: 'spring', stiffness: 320, damping: 28, mass: 0.5 }}
        >
          {carouselOrder.map((pid) => {
            const m = projectMeta[pid] || {};
            return (
              <div key={pid} className="pc-slide" style={{ width: slideWidth }}>
                <div className="pc-content">
                  <div className="pc-content-container">
                    <div className="pc-title-row">
                      <span className="pc-title">{m.title || pid}</span>
                      {/* <div className="pc-meta">
                        {m.date && <span>{m.date}</span>}
                      </div> */}
                    </div>
                    {m.description && <div className="pc-desc">{m.description}</div>}
                  </div>
                  <div>
                    <Button className="pc-button" ariaLabel={`View details for ${m.title || pid}`} onClick={() => { if (onViewDetails) onViewDetails(pid); }}>
                      <HugeiconsIcon icon={FloppyDiskIcon} size={12} />
                      View
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </motion.div>
      </div>

      <div className="pc-controls">
        <button type="button" className="pc-arrow pc-arrow-left" aria-label="Previous project" onClick={handlePrev}>
          <HugeiconsIcon icon={ArrowLeft01Icon} size={32} />
        </button>
        <div className="pc-dots" role="tablist" aria-label="Project pages">
          {carouselOrder.map((pid, i) => {
            const active = i === index;
            const title = projectMeta[pid]?.title || pid;
            return (
              <button
                key={pid}
                type="button"
                className="pc-dot"
                role="tab"
                aria-selected={active}
                aria-label={`Go to ${title}`}
                onClick={() => handleDotClick(i)}
              >
                <span className="pc-dot-rail">
                  <motion.div
                    className="pc-dot-pill"
                    layout
                    initial={false}
                    animate={{
                      backgroundColor: active ? 'var(--color-primary)' : 'var(--color-tertiary)',
                      width: active ? 16 : 8
                    }}
                    transition={{
                      backgroundColor: { duration: 0.18 },
                      width: { type: 'spring', stiffness: 640, damping: 16, mass: 0.38 }
                    }}
                  />
                </span>
              </button>
            );
          })}
        </div>
        <button type="button" className="pc-arrow pc-arrow-right" aria-label="Next project" onClick={handleNext}>
          <HugeiconsIcon icon={ArrowRight01Icon} size={32} />
        </button>
      </div>
    </div>
  );
}

export default ProjectCarousel;


